/**
 * One exact-session destination editor for assigned teaching users, edited in
 * place beneath the line it changes (object checklist item 3).
 *
 * The server supplies the action inventory and the initial intent. The editor
 * exposes only choices that inventory and the server-derived term state permit.
 * It preserves the existing manual writer, optimistic concurrency, typed 409
 * recovery, learner preview, and provider-specific operation seams. Its acts
 * are outline or quiet: the page's one filled act is the time tile's (ADR
 * 0035, decision 5).
 *
 * An unsaved entry is kept the way the session plan keeps its draft: privately
 * in this tab, with the version it was made against, so leaving the session and
 * coming back reopens the editor onto it. Closing without saving forgets it.
 */
import { useEffect, useId, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type Ref } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";

import { guidanceKey, readRecovery, writeRecovery } from "@/components/courses/guidance-recovery";
import { GoogleMeetSpaceControl } from "@/components/session/GoogleMeetSpaceControl";
import { ZoomMeetingControl } from "@/components/session/ZoomMeetingControl";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ConflictNotice, type ConflictFact } from "@/components/ui/conflict-notice";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { conflictCurrent, useSaveSessionDestination } from "@/hooks/use-session-workspace";
import type { Session } from "@/lib/api/attendance";
import { MEETING_PROVIDER_LABEL, MODALITY_LABEL } from "@/lib/api/courses";
import type { Modality } from "@/lib/api/courses";
import type { MeetingProvider } from "@/lib/api/tenant";
import type { SessionWorkspace } from "@/lib/api/sessionPortal";
import { refreshHomeAfterAct } from "@/lib/home-query-keys";
import {
  authorizedDestinationIntents,
  initialDestinationIntent,
  type DestinationIntent,
} from "@/lib/session-destination-editor";
import { ACTION_LABEL, STAFF_DESTINATION_ISSUE } from "@/lib/session-portal";

const OWNERSHIP_LABEL = {
  tenant_workspace: "An institutional account owns this meeting",
  external_or_personal: "A personal or external account owns this meeting",
  unknown: "Not stated",
} as const;

type Ownership = keyof typeof OWNERSHIP_LABEL;

/** What the form holds. Compared whole, it says whether anything is unsaved. */
interface DestinationEntry {
  intent: DestinationIntent;
  modality: Modality;
  provider: MeetingProvider | "";
  url: string;
  location: string;
  ownership: Ownership;
  note: string;
}

/** An unsaved entry kept across navigation, with the version it was made against. */
interface KeptEntry {
  entry: DestinationEntry;
  expectedUpdatedAt: string;
}

/** What the page may ask of an open editor. */
export type DestinationEditorControls = { close: () => void };

export function SessionDestinationEditor({
  id,
  workspace,
  open,
  onOpenChange,
  onGoogleMeetCompleted,
  onZoomMeetingCompleted,
  controlsRef,
}: {
  /** The editor's id, for the control that opens it. */
  id?: string;
  workspace: SessionWorkspace;
  open: boolean;
  /** The page opens the editor; the editor closes itself, and reopens onto a kept entry. */
  onOpenChange: (open: boolean) => void;
  onGoogleMeetCompleted: () => void;
  onZoomMeetingCompleted: () => void;
  controlsRef?: Ref<DestinationEditorControls>;
}) {
  const { me } = useAuth();
  const keptKey = guidanceKey(
    me?.tenant_id ?? "",
    me?.user_id ?? "",
    workspace.course.id,
    `destination-${workspace.session.id}`,
  );
  const [kept, setKept] = useState(() => readKept(keptKey));
  const [restored, setRestored] = useState(false);
  const arrived = useRef(false);

  // An entry kept from before this session was left reopens the editor onto it,
  // once, on arrival. Focus stays where the page put it.
  useLayoutEffect(() => {
    if (arrived.current) return;
    arrived.current = true;
    if (kept && !open) {
      setRestored(true);
      onOpenChange(true);
    }
  }, [kept, open, onOpenChange]);

  const closed = () => {
    forgetKept(keptKey);
    setKept(null);
    setRestored(false);
    onOpenChange(false);
  };

  if (!open) return null;
  return (
    <DestinationForm
      id={id}
      workspace={workspace}
      kept={kept}
      restored={restored}
      keptKey={keptKey}
      controlsRef={controlsRef}
      onClosed={closed}
      onGoogleMeetCompleted={onGoogleMeetCompleted}
      onZoomMeetingCompleted={onZoomMeetingCompleted}
    />
  );
}

function DestinationForm({
  id,
  workspace,
  kept,
  restored,
  keptKey,
  controlsRef,
  onClosed,
  onGoogleMeetCompleted,
  onZoomMeetingCompleted,
}: {
  id?: string;
  workspace: SessionWorkspace;
  kept: KeptEntry | null;
  /** Opened onto a kept entry on arrival, not by the user: focus is left alone. */
  restored: boolean;
  keptKey: string;
  controlsRef?: Ref<DestinationEditorControls>;
  onClosed: () => void;
  onGoogleMeetCompleted: () => void;
  onZoomMeetingCompleted: () => void;
}) {
  const { session, destination, viewer } = workspace;
  const courseDefault = destination.course_default;
  const providers = destination.enabled_providers ?? [];
  const previewId = useId();
  const allowed = viewer.allowed_actions;
  const availableIntents = useMemo(
    () => authorizedDestinationIntents(workspace),
    [workspace],
  );
  const googleAction = (["replace_google_meet", "create_google_meet"] as const)
    .find((action) => allowed.includes(action));
  const zoomAction = (["replace_zoom_meeting", "create_zoom_meeting"] as const)
    .find((action) => allowed.includes(action));
  const canInherit = availableIntents.includes("inherit");
  const canManual = availableIntents.includes("manual");

  const [baseline, setBaseline] = useState(() => entryFrom(workspace, availableIntents));
  const [entry, setEntry] = useState(() => kept?.entry ?? entryFrom(workspace, availableIntents));
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState(
    () => kept?.expectedUpdatedAt ?? session.updated_at,
  );
  // A choice the server no longer authorizes falls back to one it does.
  const intent = availableIntents.includes(entry.intent)
    ? entry.intent
    : initialDestinationIntent(workspace, availableIntents);
  const shown = useMemo<DestinationEntry>(() => ({ ...entry, intent }), [entry, intent]);
  const dirty = JSON.stringify(shown) !== JSON.stringify(baseline);
  const { modality, provider, url, location, ownership, note } = shown;
  const change = <K extends keyof DestinationEntry>(key: K, value: DestinationEntry[K]) =>
    setEntry((current) => ({ ...current, [key]: value }));

  const formRef = useRef<HTMLFormElement>(null);
  const saveRef = useRef<HTMLButtonElement>(null);
  const save = useSaveSessionDestination(session.id);
  const queryClient = useQueryClient();
  const conflict = conflictCurrent(save.error);
  const statusRef = useAnnouncedResult(save.isSuccess, save.isError);
  const remote = modality !== "in_person";
  // A saved destination is an act on the session Home shows (issue #794): its
  // reads refresh the way the roll's and the plan's do.
  const afterSaved = () => refreshHomeAfterAct(queryClient);

  useEffect(() => {
    if (!restored) focusFirst(formRef.current);
  }, [restored]);

  useEffect(() => {
    if (conflict?.updated_at) setExpectedUpdatedAt(conflict.updated_at);
  }, [conflict]);

  useEffect(() => {
    if (save.data?.updated_at) setExpectedUpdatedAt(save.data.updated_at);
  }, [save.data?.updated_at]);

  // The unsaved entry is kept while there is one, the way the plan's draft is.
  useEffect(() => {
    try {
      if (dirty) writeRecovery(keptKey, { entry: shown, expectedUpdatedAt } satisfies KeptEntry);
      else sessionStorage.removeItem(keptKey);
    } catch { /* A denied store keeps the entry on screen only. */ }
  }, [dirty, shown, expectedUpdatedAt, keptKey]);

  function close() {
    if (save.isPending) return;
    onClosed();
  }

  useImperativeHandle(controlsRef, () => ({ close }));

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (intent !== "inherit" && intent !== "manual") return;
    const submitted = shown;
    save.mutate(
      intent === "inherit"
        ? {
            modality: null,
            meeting_provider: null,
            meeting_url: null,
            location: null,
            organizer_ownership: ownership,
            note: note.trim() || null,
            expected_updated_at: expectedUpdatedAt,
          }
        : {
            modality,
            meeting_provider: provider || null,
            meeting_url: remote ? url.trim() || null : null,
            location: modality === "online" ? null : location.trim() || null,
            organizer_ownership: ownership,
            note: note.trim() || null,
            expected_updated_at: expectedUpdatedAt,
          },
      {
        onSuccess: () => {
          setBaseline(submitted);
          afterSaved();
        },
      },
    );
  }

  /** Keep this entry: the next save replaces their version on purpose. */
  function keepMine() {
    save.reset();
    saveRef.current?.focus();
  }

  /** Load their version into the form; nothing is left unsaved. */
  function loadTheirs() {
    if (!conflict) return;
    const theirs = entryFromConflict(conflict, intent, availableIntents);
    setEntry(theirs);
    setBaseline(theirs);
    if (conflict.updated_at) setExpectedUpdatedAt(conflict.updated_at);
    save.reset();
    void queryClient.invalidateQueries({ queryKey: ["session-workspace"] });
    focusFirst(formRef.current);
  }

  const managedZoomRecovery = destination.managed_zoom?.state === "unknown"
    ? destination.managed_zoom.operation === "replace"
      ? "Zoom meeting replacement has an unknown outcome. Do not retry. Use a manual meeting link and ask an operator to reconcile the provider record."
      : "Zoom meeting creation has an unknown outcome. Do not retry. Use a manual meeting link and ask an operator to reconcile the provider record."
    : destination.managed_zoom?.state === "failed"
      ? destination.managed_zoom.operation === "replace"
        ? "Zoom meeting replacement failed. You may retry or use a manual meeting link."
        : "Zoom meeting creation failed. You may retry or use a manual meeting link."
      : null;

  if (availableIntents.length === 0) return null;

  return (
    <form
      ref={formRef}
      id={id}
      aria-label="Edit where the session meets"
      onSubmit={submit}
      onKeyDown={(event) => {
        // A select or menu inside closes on its own Escape first.
        if (event.key !== "Escape" || event.nativeEvent.defaultPrevented) return;
        event.preventDefault();
        close();
      }}
      className="grid min-w-0 content-start gap-4 pt-2 pb-1"
    >
      <p className="text-meta text-muted-foreground">
        Currently in effect: {destination.effective_source === "session_override"
          ? "this exact session's own destination"
          : "the course default"}. Provider entry is not verified by saving.
      </p>

      {restored && dirty && (
        <p className="text-meta text-foreground-soft">
          Your unsaved entry from before is kept here. Save it, or close without saving.
        </p>
      )}

      {destination.destination_issue && (
        <Alert variant="destructive">
          <AlertTriangle aria-hidden strokeWidth={1.75} />
          <AlertDescription>{STAFF_DESTINATION_ISSUE[destination.destination_issue]}</AlertDescription>
        </Alert>
      )}

      {managedZoomRecovery && (
        <Alert variant="warning" role="status">
          <AlertTriangle aria-hidden strokeWidth={1.75} />
          <AlertDescription>{managedZoomRecovery}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-2">
        <p id="destination-choice-heading" className="text-meta font-semibold text-muted-foreground">
          Where this session meets
        </p>
        <RadioGroup aria-labelledby="destination-choice-heading" value={intent} onValueChange={(value) => change("intent", value as DestinationIntent)}>
          {canInherit && <IntentChoice value="inherit" title="Use the course default" detail={describeDefault(courseDefault)} />}
          {canManual && <IntentChoice value="manual" title="Enter a destination manually" detail="Applies to this session only." />}
          {googleAction && <IntentChoice value="google_meet" title={ACTION_LABEL[googleAction]} detail="Uses the separately authorized managed Google Meet operation." />}
          {zoomAction && <IntentChoice value="zoom" title={ACTION_LABEL[zoomAction]} detail="Uses the separately authorized managed Zoom operation." />}
        </RadioGroup>
      </div>

      {intent === "manual" && (
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="destination-modality">Modality</Label>
            <Select value={modality} onValueChange={(next) => change("modality", next as Modality)}>
              <SelectTrigger id="destination-modality" className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(["online", "in_person", "hybrid"] as const).map((value) => (
                  <SelectItem key={value} value={value}>{MODALITY_LABEL[value]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {remote && (
            <div className="grid gap-2">
              <Label htmlFor="destination-provider">Meeting provider</Label>
              <Select value={provider} onValueChange={(next) => change("provider", next as MeetingProvider)}>
                <SelectTrigger id="destination-provider" className="w-full"><SelectValue placeholder="Choose a provider" /></SelectTrigger>
                <SelectContent>
                  {providers.map((value) => <SelectItem key={value} value={value}>{MEETING_PROVIDER_LABEL[value]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {remote && (
            <div className="grid gap-2">
              <Label htmlFor="destination-url">Meeting link</Label>
              <Input
                id="destination-url"
                type="url"
                inputMode="url"
                placeholder="https://meet.google.com/abc-defg-hij"
                value={url}
                onChange={(event) => change("url", event.target.value)}
                aria-describedby={previewId}
              />
              <p className="text-meta text-muted-foreground">Paste the full HTTPS meeting link without a username or password.</p>
            </div>
          )}

          {modality !== "online" && (
            <div className="grid gap-2">
              <Label htmlFor="destination-location">Room or location</Label>
              <Input id="destination-location" value={location} onChange={(event) => change("location", event.target.value)} />
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="destination-ownership">Who owns this meeting room?</Label>
            <Select value={ownership} onValueChange={(next) => change("ownership", next as Ownership)}>
              <SelectTrigger id="destination-ownership" className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(OWNERSHIP_LABEL).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-meta text-muted-foreground">Recorded as your statement, not provider verification.</p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="destination-note">Correction note (optional)</Label>
            <Textarea id="destination-note" rows={2} maxLength={500} value={note} onChange={(event) => change("note", event.target.value)} />
          </div>
        </div>
      )}

      {(intent === "inherit" || intent === "manual") && (
        <LearnerPreview
          id={previewId}
          intent={intent}
          modality={intent === "inherit" ? (courseDefault?.modality ?? destination.modality) : modality}
          url={intent === "inherit" ? (courseDefault?.meeting_url ?? null) : url.trim() || null}
          location={intent === "inherit" ? (courseDefault?.location ?? null) : location.trim() || null}
        />
      )}

      {intent === "google_meet" && googleAction && (
        <GoogleMeetSpaceControl
          sessionId={session.id}
          replace={googleAction === "replace_google_meet"}
          variant="outline"
          onCompleted={() => { afterSaved(); onGoogleMeetCompleted(); onClosed(); }}
        />
      )}
      {intent === "zoom" && zoomAction && (
        <ZoomMeetingControl
          key={[
            destination.managed_zoom?.state ?? "absent",
            destination.managed_zoom?.failure_classification ?? "",
            destination.managed_zoom?.created_at ?? "",
            destination.managed_zoom?.completed_at ?? "",
          ].join(":")}
          sessionId={session.id}
          replace={zoomAction === "replace_zoom_meeting"}
          variant="outline"
          onCompleted={() => { afterSaved(); onZoomMeetingCompleted(); onClosed(); }}
          onRecoveryRequired={onZoomMeetingCompleted}
        />
      )}

      <div className="grid gap-2">
        {/* One live region for every save outcome. A conflict shows their
            version beside the entry kept in the fields; any other outcome is
            one quiet line. */}
        <div
          ref={statusRef}
          role="status"
          aria-live="polite"
          tabIndex={-1}
          className="text-ui outline-none"
        >
          {conflict
            ? (
                <ConflictNotice
                  object="where this session meets"
                  message="Someone else changed where this session meets while you were editing. Your entry is kept here. Compare, then keep yours or load theirs."
                  facts={theirFacts(conflict)}
                  disabled={save.isPending}
                  onKeepMine={keepMine}
                  onLoadTheirs={loadTheirs}
                />
              )
            : save.isSuccess
              ? <p className="text-foreground-soft">Destination saved. The updated destination is now in effect.</p>
              : save.isError
                ? <p className="text-destructive">{save.error.message}</p>
                : null}
        </div>

        <div className="flex flex-wrap gap-2">
          {(intent === "inherit" || intent === "manual") && (
            <Button ref={saveRef} type="submit" variant="outline" disabled={save.isPending}>
              {save.isPending && <Loader2 aria-hidden className="mr-1.5 size-4 animate-spin" />}
              Save where the session meets
            </Button>
          )}
          <Button type="button" variant="ghost" onClick={close} disabled={save.isPending}>
            {dirty ? "Close without saving" : save.isSuccess ? "Done" : "Close"}
          </Button>
        </div>
      </div>
    </form>
  );
}

function IntentChoice({ value, title, detail }: { value: DestinationIntent; title: string; detail: string }) {
  const id = useId();
  return (
    <Label htmlFor={id} className="flex min-h-11 cursor-pointer items-start gap-3 rounded-control px-3 py-3 text-left hover:bg-well has-[[data-state=checked]]:bg-primary-wash">
      <RadioGroupItem
        id={id}
        value={value}
        className="relative -my-1.5 size-11 border-0 shadow-none before:absolute before:top-1/2 before:left-1/2 before:size-4 before:-translate-x-1/2 before:-translate-y-1/2 before:rounded-full before:border before:border-input"
      />
      <span className="grid gap-0.5">
        <span className="text-ui font-medium text-foreground">{title}</span>
        <span className="text-meta font-normal text-muted-foreground">{detail}</span>
      </span>
    </Label>
  );
}

/** The entry the server's read implies: the intent it names and this session's own values. */
function entryFrom(workspace: SessionWorkspace, intents: DestinationIntent[]): DestinationEntry {
  const { destination } = workspace;
  const overrides = destination.overrides;
  return {
    intent: initialDestinationIntent(workspace, intents),
    modality: overrides?.modality ?? destination.modality,
    provider: overrides?.meeting_provider ?? destination.meeting_provider ?? "",
    url: overrides?.meeting_url ?? "",
    location: overrides?.location ?? "",
    ownership: "unknown",
    note: "",
  };
}

/** Their saved version as an entry: the course default when they set nothing of their own. */
function entryFromConflict(
  current: Session,
  fallback: DestinationIntent,
  intents: DestinationIntent[],
): DestinationEntry {
  const own = current.overrides;
  const followsDefault = own !== undefined && Object.values(own).every((value) => value === null);
  return {
    intent: followsDefault && intents.includes("inherit")
      ? "inherit"
      : intents.includes("manual") ? "manual" : fallback,
    modality: own?.modality ?? current.modality,
    provider: own?.meeting_provider ?? current.meeting_provider ?? "",
    url: own ? own.meeting_url ?? "" : current.meeting_url ?? "",
    location: own ? own.location ?? "" : current.location ?? "",
    ownership: "unknown",
    note: "",
  };
}

/** Their version, fact by fact, under the labels the form shows. */
function theirFacts(current: Session): ConflictFact[] {
  const facts: ConflictFact[] = [{ label: "Modality", theirs: MODALITY_LABEL[current.modality] }];
  if (current.modality !== "in_person") {
    facts.push({
      label: "Meeting provider",
      theirs: current.meeting_provider ? MEETING_PROVIDER_LABEL[current.meeting_provider] : "",
    });
    facts.push({ label: "Meeting link", theirs: current.meeting_url ?? "" });
  }
  if (current.modality !== "online") {
    facts.push({ label: "Room or location", theirs: current.location ?? "" });
  }
  return facts;
}

function describeDefault(courseDefault: SessionWorkspace["destination"]["course_default"]): string {
  if (!courseDefault) return "The course's configured destination.";
  if (courseDefault.modality === "in_person") return courseDefault.location ? `In person, ${courseDefault.location}.` : "In person.";
  return courseDefault.meeting_url
    ? `${MODALITY_LABEL[courseDefault.modality]}, ${courseDefault.meeting_provider ? MEETING_PROVIDER_LABEL[courseDefault.meeting_provider] : "a configured link"}.`
    : `${MODALITY_LABEL[courseDefault.modality]}, no link configured.`;
}

function LearnerPreview({ id, intent, modality, url, location }: {
  id: string;
  intent: "inherit" | "manual";
  modality: Modality;
  url: string | null;
  location: string | null;
}) {
  const line = modality === "in_person"
    ? location ? `In person - ${location}` : "In person"
    : url ? "Join session" : "Meeting link not available yet";
  return (
    <div id={id} className="grid gap-1.5 rounded-control bg-well p-3">
      <div className="eyebrow">Learners will see</div>
      <p className="flex flex-wrap items-baseline gap-x-2 text-ui text-foreground">
        {modality !== "in_person" && <span className="text-meta text-muted-foreground">{MODALITY_LABEL[modality]}</span>}
        <span>{line}</span>
      </p>
      <p className="text-meta text-muted-foreground">
        {intent === "inherit" ? "This session will follow the course default." : "This session will use its own destination."} The exact wording is confirmed after saving.
      </p>
    </div>
  );
}

/** Focus moves into the editor at the choice already made. */
function focusFirst(form: HTMLFormElement | null) {
  const target = form?.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]')
    ?? form?.querySelector<HTMLElement>("button:not(:disabled), input, select, textarea");
  target?.focus();
}

function readKept(key: string): KeptEntry | null {
  const kept = readRecovery<KeptEntry>(key);
  return kept && typeof kept.expectedUpdatedAt === "string" && typeof kept.entry === "object" && kept.entry
    ? kept
    : null;
}

function forgetKept(key: string) {
  try {
    sessionStorage.removeItem(key);
  } catch { /* Nothing could have been kept. */ }
}

function useAnnouncedResult(succeeded: boolean, failed: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (succeeded || failed) ref.current?.focus();
  }, [succeeded, failed]);
  return ref;
}
